import { useMemo } from 'react';

import { TClaimedCode, TDeal } from '@lootboy/lib/types/deals';

import { Divider } from '~/components/atoms/Divider';
import XIcon from '~/components/atoms/Icons/XIcon';
import { ListContainer } from '~/components/atoms/ListContainer';

import { DealsContextProvider } from '~/views/DealsView/DealsContext';
import { NoDealsWidget } from '~/views/DealsView/NoDealsWidget';
import { OfferGroup } from '~/views/DealsView/OfferGroup';

import styles from './FetchedDealsContent.module.scss';

type TFetchedDealsContentProps = {
  deal: TDeal;
  onClose: () => void;
};

const groupByOfferId = (acc: Record<string, string>, code: TClaimedCode) => {
  acc[code.dealOffer] = code.code;
  return acc;
};

export function FetchedDealsContent({
  deal,
  onClose,
}: TFetchedDealsContentProps): JSX.Element {
  // on the web we disregard the offer group structure set in the CMS
  // if there's more than one offer, the first open should take up all available space
  // and all the offers after that should be placed in a 2 column grid
  const offerGroups = useMemo(() => {
    const allOffers = deal.dealArea?.offerGroups
      ?.flatMap((offerGroup) => offerGroup?.offers)
      ?.filter(Boolean);

    if (!allOffers || !allOffers.length) {
      return undefined;
    }

    if (allOffers.length === 1) {
      return [{ offers: allOffers }];
    }
    const mainOfferGroup = allOffers.splice(0, 1);

    return [{ offers: mainOfferGroup }, { offers: allOffers }];
  }, [deal]);

  const claimedCodes = useMemo(
    () => deal?.claimedCodes?.reduce(groupByOfferId, {}),
    [deal?.claimedCodes],
  );

  return (
    <main className={styles.container}>
      <header className={styles.header}>
        {deal.dealArea.title}
        <XIcon
          background={false}
          onClick={onClose}
          className={styles.closeButton}
        />
      </header>
      <Divider className={styles.divider} />
      {offerGroups?.length ? (
        <ListContainer className={styles.listContainer}>
          <DealsContextProvider>
            {offerGroups.map((offerGroup) => (
              <OfferGroup
                key={offerGroup.offers[0]._id}
                offerGroup={offerGroup}
                claimedCodes={claimedCodes}
              />
            ))}
          </DealsContextProvider>
        </ListContainer>
      ) : (
        <NoDealsWidget />
      )}
    </main>
  );
}
